import { isoDateTodayLocal } from "../../utils/scheduleTypes";
import { toMonthlyRepeatOn } from "../../utils/storyRefreshConstants";
import type { StoryRefreshConfig } from "../../utils/storyRefreshTypes";
import {
  getMonthlyRepeatOptions,
  type MonthlyRepeatRule,
} from "../../utils/storyScheduleRecurrence";
import type {
  StoryRefreshScheduleOutApi,
  StoryRefreshScheduleResponseApi,
  StoryRefreshScheduleState,
  StoryRefreshScheduleWriteApi,
  StoryRefreshScheduleWriteRequestApi,
  StoryScheduleEndsApi,
  StoryMonthlyRuleApi,
} from "./storySchedule.types";
import { fromApiWeekdays, toApiWeekday } from "./storyScheduleWeekday";

/**
 * Translation between the modal's `StoryRefreshConfig` and the refresh-schedule
 * wire contract, in both directions.
 *
 * The two shapes differ in more than casing: the modal keeps every field for
 * every preset (so switching Repeat doesn't lose the user's work), while each
 * API variant accepts *only* its own fields and 422s on the rest. So the
 * outbound mapper builds each variant from scratch rather than spreading a
 * common base — a stale `startsOn` left over from Weekly must not ride along on
 * a Daily save.
 */

// ─── Outbound: StoryRefreshConfig → API ───────────────────────────────────

const toScheduleTime = (hour: number | null): string => {
  if (hour === null || !Number.isInteger(hour) || hour < 0 || hour > 23) {
    throw new Error("A schedule time must be selected before saving.");
  }

  return `${String(hour).padStart(2, "0")}:00`;
};

const toEnds = (config: StoryRefreshConfig): StoryScheduleEndsApi => {
  if (config.endsMode === "on") {
    return { kind: "on", date: config.endsOn };
  }
  if (config.endsMode === "after") {
    return { kind: "after", occurrences: config.endsAfter ?? 1 };
  }
  return { kind: "never" };
};

const toMonthlyRule = (config: StoryRefreshConfig): StoryMonthlyRuleApi => {
  // getMonthlyRepeatOptions returns exactly two options for a given start date:
  // [0] is the date-based rule (day_of_month or last_day), [1] the weekday one.
  const optionIndex =
    config.monthlyRepeatOn === "day-of-month" ||
    config.monthlyRepeatOn === "last-day"
      ? 0
      : 1;
  const rule = getMonthlyRepeatOptions(config.startsOn)[optionIndex]?.value;

  if (!rule) {
    throw new Error(
      "A valid monthly repeat option must be selected before saving.",
    );
  }

  if (rule.kind === "ordinal_weekday") {
    return {
      kind: "ordinal_weekday",
      ordinal: rule.ordinal,
      weekday: toApiWeekday(rule.weekday),
    };
  }

  return rule;
};

/** Builds the `repeat`-discriminated write variant for the active preset. */
export const toStoryScheduleWrite = (
  config: StoryRefreshConfig,
): StoryRefreshScheduleWriteApi => {
  const schedule_at = toScheduleTime(config.refreshHour);

  // One-off runs use `schedule_on`, and carry no `starts_on` and no `ends`.
  if (config.repeat === "none") {
    return {
      repeat: "does_not_repeat",
      schedule_on: config.scheduleOn,
      schedule_at,
      timezone: config.timezone,
    };
  }

  // Daily carries no date field whatsoever — sending one is a 422.
  if (config.repeat === "daily") {
    return { repeat: "daily", schedule_at, timezone: config.timezone };
  }

  if (config.repeat === "weekly") {
    return {
      repeat: "weekly",
      every_weeks: config.every,
      repeat_on: config.repeatOn.map(toApiWeekday),
      starts_on: config.startsOn,
      schedule_at,
      timezone: config.timezone,
      ends: toEnds(config),
    };
  }

  return {
    repeat: "monthly",
    every_months: config.every,
    monthly_rule: toMonthlyRule(config),
    starts_on: config.startsOn,
    schedule_at,
    timezone: config.timezone,
    ends: toEnds(config),
  };
};

/** Wraps the write variant in the `{ schedule }` envelope POST and PUT share. */
export const toStoryScheduleWriteRequest = (
  config: StoryRefreshConfig,
): StoryRefreshScheduleWriteRequestApi => ({
  schedule: toStoryScheduleWrite(config),
});

// ─── Inbound: API → StoryRefreshConfig ────────────────────────────────────

/**
 * `HH:mm` → hour. The modal only offers whole hours, so a backend-set `"10:30"`
 * floors to 10 — the minutes are not representable in the current UI.
 */
const toRefreshHour = (scheduleAt: string): number | null => {
  const hour = Number(scheduleAt.split(":")[0]);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? hour : null;
};

const fromApiMonthlyRule = (rule: StoryMonthlyRuleApi): MonthlyRepeatRule =>
  rule.kind === "ordinal_weekday"
    ? {
        kind: "ordinal_weekday",
        ordinal: rule.ordinal,
        weekday: fromApiWeekdays([rule.weekday])[0],
      }
    : rule;

const fromApiEnds = (
  ends: StoryScheduleEndsApi,
): Pick<StoryRefreshConfig, "endsMode" | "endsOn" | "endsAfter"> => {
  if (ends.kind === "on") {
    return { endsMode: "on", endsOn: ends.date, endsAfter: null };
  }
  if (ends.kind === "after") {
    return { endsMode: "after", endsOn: "", endsAfter: ends.occurrences };
  }
  return { endsMode: "never", endsOn: "", endsAfter: null };
};

/**
 * Neutral starting point for fields the incoming variant doesn't carry. Matches
 * the modal's own defaults so an edit-then-switch-preset behaves the same as a
 * fresh open rather than surfacing values the backend never sent.
 */
const configDefaults = (): StoryRefreshConfig => ({
  repeat: "none",
  every: 1,
  repeatOn: [],
  repeatDom: null,
  monthlyRepeatOn: null,
  scheduleOn: isoDateTodayLocal(),
  startsOn: isoDateTodayLocal(),
  refreshHour: null,
  timezone: "UTC",
  endsMode: "never",
  endsOn: "",
  endsAfter: null,
});

/** Persisted schedule → the modal's form state, ready for `initialValue`. */
export const toStoryRefreshConfig = (
  schedule: StoryRefreshScheduleOutApi,
): StoryRefreshConfig => {
  const base: StoryRefreshConfig = {
    ...configDefaults(),
    refreshHour: toRefreshHour(schedule.schedule_at),
    timezone: schedule.timezone,
  };

  if (schedule.repeat === "does_not_repeat") {
    return { ...base, repeat: "none", scheduleOn: schedule.schedule_on };
  }

  if (schedule.repeat === "daily") {
    return { ...base, repeat: "daily" };
  }

  if (schedule.repeat === "weekly") {
    return {
      ...base,
      ...fromApiEnds(schedule.ends),
      repeat: "weekly",
      every: schedule.every_weeks,
      repeatOn: fromApiWeekdays(schedule.repeat_on),
      startsOn: schedule.starts_on,
    };
  }

  const rule = fromApiMonthlyRule(schedule.monthly_rule);

  return {
    ...base,
    ...fromApiEnds(schedule.ends),
    repeat: "monthly",
    every: schedule.every_months,
    startsOn: schedule.starts_on,
    monthlyRepeatOn: toMonthlyRepeatOn(rule),
    // day_of_month states the day outright; the other rules are month-relative,
    // so the day shown in the UI comes from the start date instead.
    repeatDom:
      rule.kind === "day_of_month"
        ? rule.day
        : (Number(schedule.starts_on.split("-")[2]) || null),
  };
};

/**
 * Maps a GET/POST/PUT response. `schedule: null` — no schedule yet, or the story
 * was soft-deleted — stays `null` rather than collapsing into a default config,
 * because that null is what decides POST (create) vs. PUT (replace).
 */
export const mapStoryRefreshScheduleResponse = (
  api: StoryRefreshScheduleResponseApi,
): StoryRefreshScheduleState => {
  const schedule = api.schedule ?? null;

  return {
    storyId: api.story_id,
    schedule: schedule && {
      id: schedule.id,
      repeat: schedule.repeat,
      nextRunAt: schedule.next_run_at ?? null,
      createdAt: schedule.created_at,
      updatedAt: schedule.updated_at,
      config: toStoryRefreshConfig(schedule),
    },
  };
};
